import { PIECE_COSTS } from '../constants';
import { validateFen, fenFromPlacements } from '../logic/fen';
import * as Chess from 'chess.js';

// Stub generator: enforces king + budget; pawns only on ranks 5–7
// Budget typically comes from Game.getOpponentBudget (per-round income starts at $2 and gains +$1 every 5 rounds, summed cumulatively).
export function generateBlackSetup(roundIndex, budget = 0, rng = Math) {
  const pieces = [];
  // Place black king randomly on rank 8 back rank
  const files = 'abcdefgh';
  const kingFile = files[Math.floor(rng.random() * 8)];
  pieces.push({ type: 'k', square: `${kingFile}8` });
  const allRanks = ['5', '6', '7', '8'];
  const pawnRanks = new Set(['5', '6', '7']);
  const availableSquares = new Set();
  for (const file of files) {
    for (const rank of allRanks) {
      availableSquares.add(`${file}${rank}`);
    }
  }
  availableSquares.delete(`${kingFile}8`);

  const pool = ['q','r','r','b','b','n','n','p','p','p','p','p','p','p','p'];
  const dynamicPool = [...pool];
  const maxPawns = 8;
  let pawnsPlaced = 0;
  let remaining = Math.max(0, budget);

  const pieceCost = (type) => PIECE_COSTS[type] || 0;
  const getSquaresForType = (type) => {
    const squares = [];
    for (const square of availableSquares) {
      const rank = square[1];
      if (type === 'p' && !pawnRanks.has(rank)) continue;
      squares.push(square);
    }
    return squares;
  };

  while (remaining > 0 && availableSquares.size > 0) {
    const squaresByType = new Map();
    const candidates = [];
    for (const type of dynamicPool) {
      if (squaresByType.has(type)) {
        const squares = squaresByType.get(type);
        if (squares && squares.length > 0) candidates.push(type);
        continue;
      }
      if (type === 'p' && pawnsPlaced >= maxPawns) {
        squaresByType.set(type, null);
        continue;
      }
      const cost = pieceCost(type);
      if (cost > remaining) {
        squaresByType.set(type, null);
        continue;
      }
      const squares = getSquaresForType(type);
      if (!squares.length) {
        squaresByType.set(type, null);
        continue;
      }
      squaresByType.set(type, squares);
      candidates.push(type);
    }

    if (!candidates.length) break;

    const type = candidates[Math.floor(rng.random() * candidates.length)];
    const squares = squaresByType.get(type) || getSquaresForType(type);
    if (!squares || !squares.length) {
      continue;
    }
    const square = squares[Math.floor(rng.random() * squares.length)];

    pieces.push({ type, square });
    remaining -= pieceCost(type);
    availableSquares.delete(square);

    if (type === 'p') {
      pawnsPlaced += 1;
      const pawnIdx = dynamicPool.indexOf('p');
      if (pawnIdx !== -1) dynamicPool.splice(pawnIdx, 1);
    }
  }

  if (remaining > 0) {
    const nonKingPieces = pieces.filter(piece => piece.type !== 'k');
    const nextType = (type) => {
      switch (type) {
        case 'p':
          return rng.random() < 0.5 ? 'n' : 'b';
        case 'n':
        case 'b':
          return 'r';
        case 'r':
          return 'q';
        default:
          return null;
      }
    };

    while (remaining > 0 && nonKingPieces.length > 0) {
      const candidates = [];
      for (const piece of nonKingPieces) {
        const targetType = nextType(piece.type);
        if (!targetType) continue;
        const delta = pieceCost(targetType) - pieceCost(piece.type);
        if (delta <= 0 || delta > remaining) continue;
        candidates.push({ piece, targetType, delta });
      }

      if (!candidates.length) break;

      const chosen = candidates[Math.floor(rng.random() * candidates.length)];
      if (chosen.piece.type === 'p') pawnsPlaced = Math.max(0, pawnsPlaced - 1);
      chosen.piece.type = chosen.targetType;
      remaining -= chosen.delta;
    }
  }

  return pieces;
}

export function buildBattleFen(whitePieces, blackPieces) {
  const fen = fenFromPlacements({ whitePieces, blackPieces, sideToMove: 'w', castling: '-' });
  if (!validateFen(fen)) return null;
  // Ensure black is not already in check at start of white's move
  try {
    const parts = fen.split(' ');
    if (parts.length >= 2) {
      const fenBlackToMove = [parts[0], 'b', ...parts.slice(2)].join(' ');
      const test = new Chess();
      if (test.load(fenBlackToMove) && test.in_check()) {
        return null;
      }
    }
  } catch (_) {}
  return fen;
}

